import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import SurveyTemplate from "../SurveyTemplate";
import "../../styles/components/MultiPageSurvey.css";

const EnergyQuizPage2 = ({
  quizData,
  updateQuizData,
  onNext,
  onNavigationBack,
  currentSection,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [localData, setLocalData] = useState({
    exerciseFrequency: quizData.exerciseFrequency || "",
    coldHeatExposure: quizData.coldHeatExposure || "",
    menstrualTracking: quizData.menstrualTracking || "",
    stressLevels: quizData.stressLevels || "",
    proteinTiming: quizData.proteinTiming || "",
    recoveryPractices: quizData.recoveryPractices || [],
  });

  const recoveryOptions = [
    "Stretching", "Breathwork", "Meditation", "Massage", "Yoga", "None"
  ];

  const questions = [
    {
      id: "exerciseFrequency",
      question: "How's your movement pattern?",
      intro: "Let's optimize your training — here's what Huberman and Sims recommend.",
      options: [
        { value: "optimal", label: "💪 3-5x/week mix of strength + cardio" },
        { value: "moderate", label: "🏃 2-3x/week, mostly one type" },
        { value: "minimal", label: "🚶 1-2x/week or just walking" },
        { value: "none", label: "😴 Minimal movement" },
      ],
      info: "Both Huberman and Sims advocate for Zone 2 cardio (conversational pace) 2-3x/week plus strength training. This optimizes mitochondrial health and metabolic flexibility.",
    },
    {
      id: "coldHeatExposure",
      question: "Do you do cold/heat exposure (plunges, saunas, cold showers)?",
      intro: "Protocol-level insight: Deliberate stress exposure for adaptation.",
      options: [
        { value: "both", label: "🔥❄️ Both cold and heat regularly" },
        { value: "cold", label: "🧊 Cold showers/plunges" },
        { value: "heat", label: "🔥 Sauna/hot yoga" },
        { value: "neither", label: "🌡️ Neither" },
      ],
      info: "Huberman & Sims: Cold exposure (11 min/week total) boosts dopamine 2.5x for hours. Heat exposure increases growth hormone, heat shock proteins, and cardiovascular health.",
    },
    {
      id: "menstrualTracking",
      question: "Do you track your menstrual cycle and adjust training/nutrition?",
      intro: "Dr. Stacy Sims' core thesis from ROAR: Women are not small men.",
      options: [
        { value: "detailed", label: "📊 Yes, I adjust everything based on cycle" },
        { value: "basic", label: "📱 I track but don't adjust much" },
        { value: "awareness", label: "🤔 I'm aware but don't track formally" },
        { value: "not_applicable", label: "❌ N/A (male/post-menopause/other)" },
      ],
      info: "Sims' research: Hormonal fluctuations affect everything — protein needs, training recovery, hydration. Tracking enables precision optimization, not just symptom management.",
    },
    {
      id: "stressLevels",
      question: "How would you rate your daily stress and recovery awareness?",
      intro: "Stress isn't just mental — it's physiological and measurable.",
      options: [
        { value: "low_aware", label: "😌 Low stress, aware of my patterns" },
        { value: "moderate_aware", label: "⚖️ Moderate stress, working on management" },
        { value: "high_unaware", label: "😅 High stress, reactive not proactive" },
        { value: "overwhelmed", label: "🤯 Overwhelmed, need help" },
      ],
      info: "Chronic stress dysregulates cortisol, impacts sleep, and reduces training adaptations. HRV tracking and stress protocols are game-changers for optimization.",
    },
    {
      id: "proteinTiming",
      question: "Do you eat 30-40g protein within your first meal?",
      intro: "Dr. Sims emphasizes this especially for women — here's why.",
      options: [
        { value: "yes", label: "🥚 Yes, I prioritize morning protein" },
        { value: "sometimes", label: "🤔 Sometimes, when I remember" },
        { value: "minimal", label: "🥐 Usually carbs/coffee first" },
        { value: "unsure", label: "❓ I don't track protein intake" },
      ],
      info: "Sims' research: Early protein intake supports lean mass, balances blood sugar, and optimizes training adaptations. Critical for women's hormonal health and body composition.",
    },
    {
      id: "recoveryPractices",
      question: "What recovery practices do you use?",
      intro: "Recovery isn't optional — it's where adaptation happens.",
      type: "multiSelect",
      info: "Active recovery accelerates adaptation. Research shows breathwork, stretching, and meditation enhance training responses and reduce stress markers.",
    },
  ];

  const handleOptionSelect = (value) => {
    const currentQ = questions[currentQuestion];
    if (currentQ.type === "multiSelect") return; // handled separately
    setLocalData((prev) => ({ ...prev, [currentQ.id]: value }));
    setTimeout(() => handleNext(value), 300);
  };

  const handleRecoveryToggle = (practice) => {
    setLocalData((prev) => {
      let updated;
      if (practice === "None") {
        updated = prev.recoveryPractices.includes("None") ? [] : ["None"];
      } else {
        const without = prev.recoveryPractices.filter((p) => p !== "None");
        updated = without.includes(practice)
          ? without.filter((p) => p !== practice)
          : [...without, practice];
      }
      return { ...prev, recoveryPractices: updated };
    });
  };

  const handleNext = (overrideValue) => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      Object.keys(localData).forEach((key) => {
        const val = overrideValue !== undefined && key === questions[currentQuestion].id ? overrideValue : localData[key];
        updateQuizData(key, val);
      });
      onNext();
    }
  };

  const handleNavigationBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (onNavigationBack) {
      onNavigationBack();
    }
  };

  const currentQ = questions[currentQuestion];
  const isAnswered = currentQ.type === "multiSelect"
    ? localData.recoveryPractices.length > 0
    : localData[currentQ.id] !== "";
  const totalQuestions = 18;
  const globalIndex = 6 + currentQuestion; // page 2 starts at question 6

  return (
    <div className="survey-page-1">
      <SurveyTemplate
        introText={currentQ.intro}
        questionText={currentQ.question}
        options={currentQ.type === "multiSelect" ? [] : currentQ.options || []}
        infoSections={[{ title: "Why this matters", content: currentQ.info }]}
        onOptionSelect={handleOptionSelect}
        selectedOption={currentQ.type === "multiSelect" ? "" : localData[currentQ.id]}
        onBack={() => onNavigationBack && onNavigationBack()}
        customInput={
          currentQ.type === "multiSelect" ? (
            <div className="custom-input-wrapper" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {recoveryOptions.map((practice) => (
                <button
                  key={practice}
                  onClick={() => handleRecoveryToggle(practice)}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "10px",
                    border: localData.recoveryPractices.includes(practice)
                      ? "2px solid #3d7a3d"
                      : "2px solid #e0e0e0",
                    background: localData.recoveryPractices.includes(practice)
                      ? "rgba(61,122,61,0.08)"
                      : "#fff",
                    cursor: "pointer",
                    textAlign: "left",
                    fontSize: "15px",
                    fontWeight: localData.recoveryPractices.includes(practice) ? 600 : 400,
                    color: "#2a2d28",
                    transition: "all 0.15s ease",
                  }}
                >
                  {localData.recoveryPractices.includes(practice) ? "✓ " : ""}{practice}
                </button>
              ))}
            </div>
          ) : null
        }
        progressContainer={
          <div className="progress-container">
            <div className="question-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${((globalIndex + 1) / totalQuestions) * 100}%` }} />
              </div>
              <span className="progress-text">{globalIndex + 1} of {totalQuestions}</span>
            </div>
          </div>
        }
        navigationButtons={
          <div className="navigation-buttons">
            <button className="nav-button back-button" onClick={handleNavigationBack}>
              {t("common.back", { defaultValue: "Back" })}
            </button>
            <button className="nav-button next-button" onClick={() => handleNext()} disabled={!isAnswered}>
              {t("common.next", { defaultValue: "Next" })}
            </button>
          </div>
        }
      />
    </div>
  );
};

export default EnergyQuizPage2;
